import { useState, useEffect } from 'react';
import { claimsApi, casesApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';

const claimStatuses = ['submitted', 'under_review', 'approved', 'rejected', 'settled'];
const damageTypes = ['Storm', 'Flood', 'Fire', 'Water Leak', 'Structural', 'Accidental', 'Vandalism', 'Other'];

export default function ClaimsPage() {
  const [claims, setClaims] = useState([]);
  const [cases, setCases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClaim, setEditingClaim] = useState(null);
  const [formData, setFormData] = useState({
    case_id: '',
    claim_reference: '',
    insurer: '',
    policy_number: '',
    damage_type: '',
    damage_description: '',
    estimated_value: '',
    status: 'submitted',
    settlement_amount: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [claimsData, casesData] = await Promise.all([
        claimsApi.getAll(),
        casesApi.getAll(),
      ]);
      setClaims(claimsData);
      setCases(casesData);
    } catch (error) {
      toast.error('Failed to load claims');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      case_id: '', claim_reference: '', insurer: '', policy_number: '',
      damage_type: '', damage_description: '', estimated_value: '', status: 'submitted', settlement_amount: ''
    });
    setEditingClaim(null);
  };

  const openDialog = (claim = null) => {
    if (claim) {
      setEditingClaim(claim);
      setFormData({
        case_id: claim.case_id || '',
        claim_reference: claim.claim_reference || '',
        insurer: claim.insurer || '',
        policy_number: claim.policy_number || '',
        damage_type: claim.damage_type || '',
        damage_description: claim.damage_description || '',
        estimated_value: claim.estimated_value?.toString() || '',
        status: claim.status || 'submitted',
        settlement_amount: claim.settlement_amount?.toString() || '',
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null,
      settlement_amount: formData.settlement_amount ? parseFloat(formData.settlement_amount) : null,
    };
    try {
      if (editingClaim) {
        await claimsApi.update(editingClaim.id, submitData);
        toast.success('Claim updated');
      } else {
        await claimsApi.create(submitData);
        toast.success('Claim created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save claim');
    }
  };

  const handleDelete = async (claim) => {
    if (!window.confirm('Delete this claim?')) return;
    try {
      await claimsApi.delete(claim.id);
      toast.success('Claim deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete claim');
    }
  };

  const getCaseNumber = (caseId) => {
    const caseItem = cases.find(c => c.id === caseId);
    return caseItem?.case_number || '-';
  };

  const formatCurrency = (val) => val ? `£${parseFloat(val).toLocaleString()}` : '-';

  const columns = [
    { key: 'claim_number', label: 'Claim #' },
    { key: 'case_id', label: 'Case', render: (val) => getCaseNumber(val) },
    { key: 'insurer', label: 'Insurer', render: (val) => val || '-' },
    { key: 'damage_type', label: 'Damage Type', render: (val) => val || '-' },
    { key: 'estimated_value', label: 'Est. Value', render: formatCurrency },
    { key: 'settlement_amount', label: 'Settlement', render: formatCurrency },
    { key: 'status', label: 'Status', render: (val) => <StatusBadge status={val} /> },
    { key: 'created_at', label: 'Created', render: (val) => val ? format(new Date(val), 'MMM d, yyyy') : '-' },
  ];

  const actions = [
    { label: 'Edit', icon: Pencil, onClick: openDialog },
    { label: 'Delete', icon: Trash2, onClick: handleDelete, destructive: true },
  ];

  return (
    <div className="space-y-6" data-testid="claims-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Claims</h1>
          <p className="text-sm text-stone-500 mt-1">Manage insurance claims</p>
        </div>
        <Button onClick={() => openDialog()} className="bg-emerald-900 hover:bg-emerald-800" data-testid="add-claim-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Claim
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={claims}
        actions={actions}
        isLoading={isLoading}
        emptyMessage="No claims found. Add your first claim!"
      />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="claim-dialog">
          <DialogHeader>
            <DialogTitle>{editingClaim ? 'Edit Claim' : 'Create New Claim'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="case">Linked Case *</Label>
              <Select value={formData.case_id} onValueChange={(val) => setFormData({ ...formData, case_id: val })}>
                <SelectTrigger data-testid="claim-case-select">
                  <SelectValue placeholder="Select case" />
                </SelectTrigger>
                <SelectContent>
                  {cases.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.case_number} - {c.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="claim_reference">Claim Reference</Label>
                <Input
                  id="claim_reference"
                  value={formData.claim_reference}
                  onChange={(e) => setFormData({ ...formData, claim_reference: e.target.value })}
                  data-testid="claim-reference-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="policy_number">Policy Number</Label>
                <Input
                  id="policy_number"
                  value={formData.policy_number}
                  onChange={(e) => setFormData({ ...formData, policy_number: e.target.value })}
                  data-testid="claim-policy-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="insurer">Insurer</Label>
                <Input
                  id="insurer"
                  value={formData.insurer}
                  onChange={(e) => setFormData({ ...formData, insurer: e.target.value })}
                  data-testid="claim-insurer-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="damage_type">Damage Type</Label>
                <Select value={formData.damage_type} onValueChange={(val) => setFormData({ ...formData, damage_type: val })}>
                  <SelectTrigger data-testid="claim-damage-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {damageTypes.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="estimated_value">Est. Value (£)</Label>
                <Input
                  id="estimated_value"
                  type="number"
                  step="0.01"
                  value={formData.estimated_value}
                  onChange={(e) => setFormData({ ...formData, estimated_value: e.target.value })}
                  data-testid="claim-estimated-value-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="settlement_amount">Settlement (£)</Label>
                <Input
                  id="settlement_amount"
                  type="number"
                  step="0.01"
                  value={formData.settlement_amount}
                  onChange={(e) => setFormData({ ...formData, settlement_amount: e.target.value })}
                  data-testid="claim-settlement-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                  <SelectTrigger data-testid="claim-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {claimStatuses.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="damage_description">Damage Description</Label>
              <Textarea
                id="damage_description"
                value={formData.damage_description}
                onChange={(e) => setFormData({ ...formData, damage_description: e.target.value })}
                rows={3}
                data-testid="claim-description-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800" data-testid="claim-submit-btn">
                {editingClaim ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
